import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { usePushNotificationContext } from '../../contexts/PushNotificationContext';
import { Button } from '../../components/ui/button';
import { 
  Root as Card, 
  Content as CardContent, 
  Header as CardHeader, 
  Title as CardTitle 
} from '../../components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '../../components/ui/dialog';
import {
  Trash2,
  Settings2,
  AlertTriangle,
  Bell,
  BellOff
} from 'lucide-react';
import toast from 'react-hot-toast';

const SettingsPage = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    enablePushNotifications,
    disablePushNotifications
  } = usePushNotificationContext();

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await api.delete('/auth/me');
      toast.success('Your account has been deleted');
      setShowDeleteDialog(false);
      logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to delete account', error);
      toast.error(error.response?.data?.error || 'Failed to delete account. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Settings2 className="h-5 w-5 sm:h-6 sm:w-6" />
            Settings
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage your account settings</p>
        </div>
      </div>

      {/* Push Notification Settings */}
      <Card className="border border-gray-200 shadow-mobile hover:shadow-mobile-lg transition-all duration-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-base sm:text-lg font-medium text-gray-900 flex items-center gap-2">
            <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
            Push Notifications
          </CardTitle>
          <p className="text-sm text-gray-500">Get notified about orders, sales, and updates</p>
        </CardHeader>
        <CardContent>
          {!isSupported ? (
            <div className="p-4 border border-yellow-200 rounded-lg bg-yellow-50">
              <p className="text-sm text-yellow-800">
                Push notifications are not supported in your browser. Please use Chrome, Firefox, or Edge.
              </p>
            </div>
          ) : permission === 'denied' ? (
            <div className="p-4 border border-red-200 rounded-lg bg-red-50">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-red-900">Notifications Blocked</h4>
                  <p className="text-sm text-red-700 mt-1">
                    Push notifications are blocked in your browser settings. To enable them:
                  </p>
                  <ol className="text-sm text-red-700 mt-2 ml-4 list-decimal space-y-1">
                    <li>Click the lock icon in your browser's address bar</li>
                    <li>Find "Notifications" in the permissions list</li>
                    <li>Change the setting to "Allow"</li>
                    <li>Refresh this page</li>
                  </ol>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Enable Push Notifications</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {isSubscribed 
                      ? 'You will receive push notifications on this device' 
                      : 'Turn on to receive notifications about orders and updates'}
                  </p>
                </div>
                <button
                  onClick={async () => {
                    if (isSubscribed) {
                      const success = await disablePushNotifications();
                      if (success) {
                        toast.success('Push notifications disabled');
                      } else {
                        toast.error('Failed to disable notifications');
                      }
                    } else {
                      const success = await enablePushNotifications();
                      if (success) {
                        toast.success('Push notifications enabled');
                      } else {
                        toast.error('Failed to enable notifications');
                      }
                    }
                  }}
                  disabled={isLoading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                    isSubscribed ? 'bg-primary-500' : 'bg-gray-300'
                  } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${
                      isSubscribed ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {isSubscribed && (
                <div className="flex items-center space-x-2 text-sm text-green-600 dark:text-green-400">
                  <Bell className="h-4 w-4" />
                  <span>Notifications are active on this device</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200 shadow-mobile hover:shadow-mobile-lg transition-all duration-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-base sm:text-lg font-medium text-red-600 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5" />
            Danger Zone
          </CardTitle>
          <p className="text-sm text-gray-500">Irreversible and destructive actions</p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border border-red-200 rounded-lg bg-red-50">
            <div className="space-y-1">
              <h4 className="text-sm font-medium text-red-900">Delete Account</h4>
              <p className="text-sm text-red-700">
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
            </div>
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <DialogTrigger asChild>
                <Button 
                  variant="destructive" 
                  size="sm"
                  className="w-full sm:w-auto"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Account
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-red-600">
                    <AlertTriangle className="h-5 w-5" />
                    Delete Account
                  </DialogTitle>
                  <DialogDescription className="text-gray-600">
                    Are you sure you want to delete your account? This action is permanent and cannot
                    be undone. Your profile, cart, wishlist, and notification subscriptions will be lost.
                  </DialogDescription>
                </DialogHeader>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 my-4">
                  <p className="text-sm text-red-800 font-medium">This will permanently:</p>
                  <ul className="text-sm text-red-700 mt-1 ml-4 list-disc">
                    <li>Delete your profile and account data</li>
                    <li>Cancel any active push notification subscriptions</li>
                    <li>Delete your wishlist and cart</li>
                  </ul>
                  <p className="text-sm text-red-700 mt-2">
                    Your past order history is kept for our records and won't be affected.
                  </p>
                </div>
                <DialogFooter className="flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteDialog(false)}
                    disabled={isDeleting}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDeleteAccount}
                    disabled={isDeleting}
                    className="w-full sm:w-auto"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {isDeleting ? 'Deleting...' : 'Yes, Delete Account'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;